import { Briefcase, BriefcaseBusiness, Copy, Edit, ExternalLink, Eye, Plus, Search, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import Spinner from '../../components/Spinner';
import { useJobs, useJobBoardSettings } from '../../hooks/useJobBoard';
import { JobDto } from '../../types/jobBoard';
import { toast } from 'react-toastify';

const date = (value?: string) => value ? new Date(value).toLocaleDateString() : '-';
const status = (job: JobDto) => job.status ?? (job.expirationDate && new Date(job.expirationDate) < new Date() ? 'Closed' : job.publishedDate || job.publishedDate ? 'Published' : 'Draft');

export default function AdminJobsPage() {
  const jobs = useJobs();
  const settings = useJobBoardSettings();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] =
  useState("published-desc");

  const pageSize = 10;

  const filtered = useMemo(
    () =>
      [...(jobs.data ?? [])]
        .filter(
          (job) =>
            `${job.title} ${
              job.industryNames?.join(" ") ?? ""
            } ${
              job.countryNames?.join(" ") ?? ""
            }`
              .toLowerCase()
              .includes(search.toLowerCase()) &&
            (filter === "All" || status(job) === filter)
        )
        .sort((a, b) => {
          switch (sortBy) {
            case "title-asc":
              return a.title.localeCompare(b.title);

            case "title-desc":
              return b.title.localeCompare(a.title);

            case "expires-asc":
              return (
                new Date(a.expirationDate ?? "").getTime() -
                new Date(b.expirationDate ?? "").getTime()
              );

            case "applicants-desc":
              return (
                (b.applicantCount ??
                  b.applicants?.length ??
                  0) -
                (a.applicantCount ??
                  a.applicants?.length ??
                  0)
              );

            case "published-asc":
              return (
                new Date(a.publishedDate ?? "").getTime() -
                new Date(b.publishedDate ?? "").getTime()
              );

            default:
              return (
                new Date(b.publishedDate ?? "").getTime() -
                new Date(a.publishedDate ?? "").getTime()
              );
          }
        }),
    [jobs.data, search, filter, sortBy]
  );

  const visible = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const pages = Math.max(
    1,
    Math.ceil(filtered.length / pageSize)
  );

  if (jobs.isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner />
      </div>
    );
  }

  const handleCopyPublicUrl = async (jobId: string | number) => {
    const slug = settings.data?.jobBoardSlug;

    if (!slug) {
      toast.error("Public job board URL is not available.");
      return;
    }

    const publicUrl = `${window.location.origin}/jobs/${slug}/${jobId}`;

    try {
      await navigator.clipboard.writeText(publicUrl);

      toast.success("Public job URL copied!");
    } catch (error) {
      console.error("Failed to copy public job URL:", error);

      toast.error("Failed to copy public job URL.");
    }
  };

  return (
    <div className="space-y-6 text-left">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-strivre-navy flex items-center gap-2">
            <BriefcaseBusiness size={22}/>
            Manage Jobs
          </h1>

          <p className="text-sm text-strivre-mutedtext mt-1">
            Create, publish, and monitor your open positions.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={`/jobs/${settings.data?.jobBoardSlug}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`btn-secondary btn-md inline-flex items-center justify-center gap-2 rounded-lg ${
              !settings.data?.jobBoardSlug
                ? "pointer-events-none opacity-40"
                : ""
            }`}
            title="View Public Job Board"
          >
            <ExternalLink size={17} />
            View Job Board
          </Link>

          <Link
            to="/admin/job-board/jobs/create"
            className="btn-primary btn-md inline-flex items-center justify-center gap-2 rounded-lg"
          >
            <Plus size={17} />
            Create Job
          </Link>
        </div>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search
            size={17}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />

          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search jobs, industry, or country"
            className="input-base pl-10"
          />
        </div>

        <select
          className="input-base sm:w-44"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(1);
          }}
        >
          <option>All</option>
          <option>Draft</option>
          <option>Published</option>
          <option>Closed</option>
        </select>
        <select
          className="input-base sm:w-52"
          value={sortBy}
          onChange={(e) => {
            setSortBy(e.target.value);
            setPage(1);
          }}
        >
          <option value="published-desc">
            Latest Published
          </option>

          <option value="published-asc">
            Oldest Published
          </option>

          <option value="title-asc">
            Job Title A-Z
          </option>

          <option value="title-desc">
            Job Title Z-A
          </option>

          <option value="expires-asc">
            Expiring Soon
          </option>

          <option value="applicants-desc">
            Most Applicants
          </option>
        </select>
      </div>

      <div className="overflow-hidden rounded-lg border border-strivre-lightnavy bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-4">Job</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Published</th>
                <th className="px-5 py-4">Expires</th>
                <th className="px-5 py-4">Applicants</th>
                <th className="px-5 py-4 text-right">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {visible.map((job) => (
                <tr
                  key={job.jobId}
                  className="hover:bg-slate-50"
                >
                  <td className="px-5 py-4">
                    <p className="font-semibold text-slate-900">
                      {job.title}
                    </p>

                    <p className="mt-1 text-xs text-slate-500">
                      {job.industryNames?.join(", ") ||
                        "Industry"}
                      {" • "}
                      {job.countryNames?.join(", ") ||
                        "Country"}
                    </p>
                  </td>

                  <td className="px-5 py-4">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        status(job) === "Published"
                          ? "bg-emerald-50 text-emerald-700"
                          : status(job) === "Closed"
                          ? "bg-slate-100 text-slate-600"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {status(job)}
                    </span>
                  </td>

                  <td className="px-5 py-4 text-slate-600">
                    {date(job.publishedDate)}
                  </td>

                  <td className="px-5 py-4 text-slate-600">
                    {date(job.expirationDate)}
                  </td>

                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1.5 text-slate-600">
                      <Users size={15} />
                      {job.applicants?.length ?? 0}
                    </span>
                  </td>

                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      <Link
                        to={`/admin/job-board/jobs/${job.jobId}`}
                        title="View Job"
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:text-strivre-burgundy"
                      >
                        <Eye size={16} />
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleCopyPublicUrl(job.jobId)}
                        disabled={!settings.data?.jobBoardSlug}
                        title="Copy Public Job URL"
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:text-strivre-burgundy disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Copy size={16} />
                      </button>
                      <Link
                        to={`/admin/job-board/jobs/${job.jobId}/edit`}
                        title="Edit Job"
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:text-strivre-burgundy"
                      >
                        <Edit size={16} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!visible.length && (
          <div className="p-12 text-center text-sm text-slate-500">
            <Briefcase
              className="mx-auto mb-3 text-slate-300"
              size={34}
            />
            No jobs match your filters.
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 text-sm text-slate-500">
          <span>
            {filtered.length}{" "}
            {filtered.length === 1 ? "job" : "jobs"}
          </span>

          <div className="flex items-center gap-2">
            <button
              disabled={page === 1}
              onClick={() =>
                setPage((current) => current - 1)
              }
              className="btn-outline btn-sm rounded-lg disabled:opacity-40"
            >
              Previous
            </button>

            <span>
              {page} / {pages}
            </span>

            <button
              disabled={page === pages}
              onClick={() =>
                setPage((current) => current + 1)
              }
              className="btn-outline btn-sm rounded-lg disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}